<script lang="ts" context="module">
  const getCurrentEditor = () => getActiveComponent('DataDeployTab');

  registerCommand({
    id: 'dataDeploy.run',
    category: 'Data deployer',
    name: 'Deploy into DB',
    keyText: 'F5 | CtrlOrCommand+Enter',
    toolbar: true,
    isRelatedToTab: true,
    icon: 'icon run',
    testEnabled: () => getCurrentEditor()?.canRunDeploy(),
    onClick: () => getCurrentEditor().runDeploy(),
  });
  registerCommand({
    id: 'dataDeploy.kill',
    category: 'Data deployer',
    icon: 'icon close',
    name: 'Kill deploy',
    toolbar: true,
    isRelatedToTab: true,
    testEnabled: () => getCurrentEditor()?.canKillDeploy(),
    onClick: () => getCurrentEditor().killDeploy(),
  });
  registerCommand({
    id: 'dataDeploy.generateShell',
    category: 'Data deployer',
    icon: 'img shell',
    name: 'Generate shell',
    toolbar: true,
    isRelatedToTab: true,
    testEnabled: () => getCurrentEditor()?.canRunDeploy(),
    onClick: () => getCurrentEditor().generateShell(),
  });
  registerCommand({
    id: 'dataDeploy.generateSql',
    category: 'Data deployer',
    icon: 'img sql-file',
    name: 'Generate SQL',
    toolbar: true,
    isRelatedToTab: true,
    testEnabled: () => getCurrentEditor()?.canRunDeploy(),
    onClick: () => getCurrentEditor().generateSql(),
  });

  registerFileCommands({
    idPrefix: 'datadeploy',
    category: 'Data deploy',
    getCurrentEditor,
    folder: 'datadeploy',
    format: 'json',
    fileExtension: 'datadeploy',
    // undoRedo: true,
  });
</script>

<script lang="ts">
  // *** This file is part of DbGate Premium ***

  import _ from 'lodash';
  import VerticalSplitter from '../elements/VerticalSplitter.svelte';
  import FormProviderCore from '../forms/FormProviderCore.svelte';
  import useEditorData from '../query/useEditorData';
  import ToolStripContainer from '../buttons/ToolStripContainer.svelte';
  import ToolStripButton from '../buttons/ToolStripButton.svelte';
  import HorizontalSplitter from '../elements/HorizontalSplitter.svelte';
  import WidgetColumnBar from '../widgets/WidgetColumnBar.svelte';
  import WidgetColumnBarItem from '../widgets/WidgetColumnBarItem.svelte';
  import WidgetsInnerContainer from '../widgets/WidgetsInnerContainer.svelte';
  import DataDeploySettings from '../widgets/DataDeploySettings.svelte';
  import { changeTab } from '../utility/common';
  import TableControl from '../elements/TableControl.svelte';
  import { useArchiveFiles, useConnectionInfo, useDatabaseInfo } from '../utility/metadataLoaders';
  import FontIcon from '../icons/FontIcon.svelte';
  import TabControl from '../elements/TabControl.svelte';
  import TableDataGrid from '../datagrid/TableDataGrid.svelte';
  import { writable } from 'svelte/store';
  import { changeSetToSql, createGridCache, createGridConfig, createMergedRowsChangeSet } from 'dbgate-datalib';
  import JslDataGrid from '../datagrid/JslDataGrid.svelte';
  import { apiCall, apiOff, apiOn } from '../utility/api';
  import { scriptToSql, selectKeysFromTable } from 'dbgate-sqltree';
  import Link from '../elements/Link.svelte';
  import { showModal } from '../modals/modalTools';
  import ConfirmModal from '../modals/ConfirmModal.svelte';
  import { showSnackbarSuccess } from '../utility/snackbar';
  import { findEngineDriver, ScriptWriter, ScriptWriterJson } from 'dbgate-tools';
  import { extensions, getCurrentConfig } from '../stores';
  import { getBoolSettingsValue } from '../settings/settingsTools';
  import ConfirmSqlModal from '../modals/ConfirmSqlModal.svelte';
  import ErrorMessageModal from '../modals/ErrorMessageModal.svelte';
  import createActivator, { getActiveComponent } from '../utility/createActivator';
  import registerCommand from '../commands/registerCommand';
  import ToolStripCommandButton from '../buttons/ToolStripCommandButton.svelte';
  import { extractShellConnection } from '../impexp/createImpExpScript';
  import useTimerLabel from '../utility/useTimerLabel';
  import openNewTab from '../utility/openNewTab';
  import useEffect from '../utility/useEffect';
  import SocketMessageView from '../query/SocketMessageView.svelte';
  import invalidateCommands from '../commands/invalidateCommands';
  import { registerFileCommands } from '../commands/stdCommands';
  import ToolStripSaveButton from '../buttons/ToolStripSaveButton.svelte';
  import ConfirmDataDeployModal from '../modals/ConfirmDataDeployModal.svelte';
  import LoadingInfo from '../elements/LoadingInfo.svelte';

  let settingsVisible = true;
  let reloadOverlayDataToken = 0;
  let reloadDatabaseDataToken = 0;
  let progressMessage = null;

  let selectedTableName = null;
  export let tabid;
  export let savedFile;
  export let savedFilePath;

  const { editorValue, setEditorData } = useEditorData({
    tabid,
  });

  const values = {
    ...editorValue,
    update: setEditorData,
    set: setEditorData,
  };

  $: checkedTables = $values?.checkedTables || [];

  let busy = false;
  let runnerId = null;
  let executeNumber = 0;

  export const activator = createActivator('DataDeployTab', true);

  const timerLabel = useTimerLabel();

  function changeTable(tableName, changeFunc) {
    setEditorData(old => ({
      ...old,
      tables: {
        ...old?.tables,
        [tableName]: changeFunc(old?.tables?.[tableName]),
      },
    }));
    // if (selectedTable?.name == tableName) {
    //   selectedTable = {
    //     ...selectedTable,
    //     ...changeFunc(selectedTable),
    //   };
    // }
  }

  async function loadSelectedTable(table, _token) {
    selectedTableFileData = null;
    databaseKeysFromSelectedData = null;

    if (!table) {
      return;
    }

    if (table?.file && table?.table) {
      selectedTableFileData = await apiCall('archive/get-archive-data', {
        folder: archiveFolder,
        file: table?.file,
      });

      if (selectedTable.matchColumns.length > 0) {
        if (selectedTableFileData.length > 0) {
          const select = selectKeysFromTable({
            pureName: table.pureName,
            schemaName: table.schemaName,
            keyColumns: table.matchColumns,
            loadKeys: selectedTableFileData.map(row => table.matchColumns.map(col => row[col])),
          });
          const keyResp = await apiCall('database-connections/sql-select', {
            conid,
            database,
            select,
            auditLogSessionGroup: 'deployer-keys',
          });
          databaseKeysFromSelectedData = keyResp.rows.map(row => table.matchColumns.map(col => row[col]));
        } else {
          databaseKeysFromSelectedData = [];
        }
        // console.log('LOADED KEY DATA', databaseKeysFromSelectedData);
      }
    }

    if (!extractedJslIds?.[archiveFolder]?.[table.file]) {
      if (archiveFolder.endsWith('.zip')) {
        const resp = await apiCall('jsldata/download-jsl-data', {
          uri: `zip://archive:${archiveFolder}//${table.file}.jsonl`,
        });
        extractedJslIds = {
          ...extractedJslIds,
          [archiveFolder]: {
            ...extractedJslIds?.[archiveFolder],
            [table.file]: resp.jslid,
          },
        };
      }
    }
  }

  $: conid = $values?.conid;
  $: database = $values?.database;
  $: archiveFolder = $values?.archiveFolder;
  $: isArchiveReadOnly = $values?.archiveFolder?.endsWith('.zip');

  $: connection = useConnectionInfo({ conid });
  $: dbinfo = useDatabaseInfo({ conid, database });

  $: archiveFiles = useArchiveFiles({ folder: archiveFolder });

  $: pairedNames = _.sortBy(
    _.intersectionBy(
      $dbinfo?.tables?.map(x => x.pureName),
      $archiveFiles?.map(x => x.name),
      (x: string) => _.toUpper(x)
    )
  );

  $: onlyDbNames = _.sortBy(
    _.differenceBy(
      $dbinfo?.tables?.map(x => x.pureName),
      $archiveFiles?.map(x => x.name),
      (x: string) => _.toUpper(x)
    )
  );

  $: onlyArchiveNames = _.sortBy(
    _.differenceBy(
      $archiveFiles?.map(x => x.name),
      $dbinfo?.tables?.map(x => x.pureName),
      (x: string) => _.toUpper(x)
    )
  );

  function mapName(name, items, dbTables, files) {
    const item = items?.[name];
    const tableInfo = dbTables?.find(x => x.pureName?.toUpperCase() == name.toUpperCase());
    const matchColumns = item?.matchColumns ?? [];
    if (matchColumns.length == 0) {
      const matchColumn1 = tableInfo?.primaryKey?.columns?.[0]?.columnName ?? tableInfo?.columns?.[0]?.columnName;
      matchColumns.push(matchColumn1);
    }
    const file = files?.find(x => x.name.toUpperCase() == name.toUpperCase())?.name;

    return {
      ...item,
      name,
      matchColumns,
      file,
      table: tableInfo?.schemaName ? `${tableInfo?.schemaName}.${tableInfo?.pureName}` : tableInfo?.pureName,
      schemaName: tableInfo?.schemaName,
      pureName: tableInfo?.pureName,
      operationKey: JSON.stringify({
        findExisting: item?.findExisting,
        createNew: item?.createNew,
        updateExisting: item?.updateExisting,
        findCondition: item?.findCondition,
        createCondition: item?.createCondition,
        updateCondition: item?.updateCondition,
        deleteMissing: item?.deleteMissing,
        deleteRestrictionColumns: item?.deleteRestrictionColumns,
      }),
      tableInfo,
    };
  }

  async function saveCellsToArchive(columns, rows) {
    const mergedRows = rows.map(x => _.pick(x, _.uniq([...columns, ...selectedTable.matchColumns])));

    const res = await apiCall('archive/modify-file', {
      folder: archiveFolder,
      file: selectedTable.file,
      mergedRows,
      mergeKey: selectedTable.matchColumns,
      mergeMode: 'merge',
    });
    if (res) {
      showSnackbarSuccess(`Merged ${mergedRows.length} rows into ${selectedTable.file} in archive ${archiveFolder}`);
      reloadOverlayDataToken += 1;
    }
  }

  async function saveCellsToDatabase(cells, columns, rows) {
    const driver = findEngineDriver($connection, $extensions);

    const updatedRows = _.uniq(
      cells.filter(x => x.rowStatus.status == 'updated').map(x => ({ ...x.rowData, ...x.rowStatus.overlayFields }))
    ).map(x => _.pick(x, _.uniq([...columns, ...selectedTable.matchColumns])));
    const insertedRows = _.uniq(
      cells.filter(x => x.rowStatus.status == 'inserted').map(x => ({ ...x.rowData, ...x.rowStatus.overlayFields }))
    ).map(x => _.pick(x, _.uniq([...columns, ...selectedTable.matchColumns])));
    const changeSet = createMergedRowsChangeSet(
      selectedTable.tableInfo,
      updatedRows,
      insertedRows,
      selectedTable.matchColumns
    );
    const script = driver.createSaveChangeSetScript(changeSet, $dbinfo, () =>
      changeSetToSql(changeSet, $dbinfo, driver.dialect)
    );
    const sql = scriptToSql(driver, script);
    if (getBoolSettingsValue('skipConfirm.tableDataSave', false)) {
      handleConfirmSql(sql);
    } else {
      showModal(ConfirmSqlModal, {
        sql,
        onConfirm: confirmedSql => handleConfirmSql(confirmedSql),
        engine: driver.engine,
        skipConfirmSettingKey: 'skipConfirm.tableDataSave',
      });
    }
  }

  async function handleConfirmSql(sql) {
    const resp = await apiCall('database-connections/run-script', { conid, database, sql, useTransaction: true });
    const { errorMessage } = resp || {};
    if (errorMessage) {
      showModal(ErrorMessageModal, { title: 'Error when saving', message: errorMessage });
    } else {
      reloadDatabaseDataToken += 1;
      showSnackbarSuccess('Saved to database');
    }
  }

  export function getData() {
    return $values || {};
  }

  function compactCondition(condition: { [key: string]: string }) {
    if (!condition) return undefined;
    const entries = Object.entries(condition).filter(([_key, value]) => value?.trim());
    if (entries.length == 0) return undefined;
    return _.fromPairs(entries);
  }

  function createScript(typePreference: 'script' | 'json' = undefined, mergeOptions = {}) {
    const config = getCurrentConfig();
    let ScriptType = config.allowShellScripting ? ScriptWriter : ScriptWriterJson;
    if (typePreference == 'script') {
      ScriptType = ScriptWriter;
    } else if (typePreference == 'json') {
      ScriptType = ScriptWriterJson;
    }

    const script = new ScriptType();
    script.dataReplicator({
      connection: extractShellConnection($connection, database),
      archive: archiveFolder,
      items: matchedTableRows
        .filter(x => checkedTables.includes(x.name))
        .map(row => ({
          name: row.name,
          findExisting: row.findExisting,
          createNew: row.createNew,
          updateExisting: row.updateExisting,
          findCondition: compactCondition(row.findCondition),
          createCondition: compactCondition(row.createCondition),
          updateCondition: compactCondition(row.updateCondition),
          matchColumns: row.matchColumns,
          deleteMissing: row.deleteMissing,
          deleteRestrictionColumns: row.deleteRestrictionColumns,
        })),
      options: {
        rollbackAfterFinish: !!$values?.rollbackAfterFinish,
        skipRowsWithUnresolvedRefs: !!$values?.skipRowsWithUnresolvedRefs,
        setNullForUnresolvedNullableRefs: !!$values?.setNullForUnresolvedNullableRefs,
        ...mergeOptions,
      },
    });
    return script.getScript();
  }

  export function canRunDeploy() {
    return !!matchedTableRows.find(x => checkedTables.includes(x.name)) && !busy;
  }

  export async function runDeploy() {
    if (busy) return;
    showModal(ConfirmDataDeployModal, {
      onConfirm: async ({ rollbackAfterFinish }) => {
        executeNumber += 1;
        busy = true;
        const script = await createScript('json', { rollbackAfterFinish });
        let runid = runnerId;
        const resp = await apiCall('runners/start', { script });
        runid = resp.runid;
        runnerId = runid;
        timerLabel.start();
      },
      rollbackAfterFinish: $values?.rollbackAfterFinish,
      items: matchedTableRows
        .filter(x => checkedTables.includes(x.name))
        .map(row => ({
          name: row.name,
          actions: getAllOperationLabels(row),
        })),
    });
  }

  export async function generateShell() {
    const code = await createScript('script');
    openNewTab(
      {
        title: 'Shell #',
        icon: 'img shell',
        tabComponent: 'ShellTab',
      },
      { editor: code }
    );
  }

  export async function generateSql() {
    try {
      progressMessage = 'Generating SQL...';
      const script = createScript('json', { generateSqlScript: true, runid: { $replace: 'runid' } });
      const resp = await apiCall('runners/script-result', { script });

      if (resp.errorMessage) {
        showModal(ErrorMessageModal, { title: 'Error when generating SQL', message: resp.errorMessage });
        return;
      }

      openNewTab(
        {
          title: 'Query #',
          icon: 'img sql-file',
          tabComponent: 'QueryTab',
          props: {
            conid,
            database,
          },
        },
        { editor: resp }
      );
    } finally {
      progressMessage = null;
    }
  }

  $: effect = useEffect(() => registerRunnerDone(runnerId));

  function registerRunnerDone(rid) {
    if (rid) {
      apiOn(`runner-done-${rid}`, handleRunnerDone);
      return () => {
        apiOff(`runner-done-${rid}`, handleRunnerDone);
      };
    } else {
      return () => {};
    }
  }

  $: $effect;

  const handleRunnerDone = () => {
    busy = false;
    timerLabel.stop();

    reloadOverlayDataToken += 1;
    reloadDatabaseDataToken += 1;
  };

  export function canKillDeploy() {
    return busy;
  }

  export function killDeploy() {
    apiCall('runners/cancel', {
      runid: runnerId,
    });
    timerLabel.stop();
  }

  $: tableRows = [
    {
      group: 'Matched',
      rows: pairedNames.map(name => mapName(name, $editorValue?.tables, $dbinfo?.tables, $archiveFiles)),
    },
    {
      group: 'Only in archive',
      rows: onlyArchiveNames.map(name => mapName(name, $editorValue?.tables, $dbinfo?.tables, $archiveFiles)),
    },
    {
      group: 'Only in database',
      rows: onlyDbNames.map(name => mapName(name, $editorValue?.tables, $dbinfo?.tables, $archiveFiles)),
    },
  ];

  $: matchedTableRows = tableRows[0].rows;

  $: flatTableRows = tableRows.map(x => x.rows).flat();

  $: selectedTable = flatTableRows.find(x => x.name == selectedTableName);

  $: {
    busy;
    runnerId;
    tableRows;
    selectedTable;
    invalidateCommands();
  }

  // $: {
  //   if (flatTableRows?.length > 0 && (!selectedTable || !flatTableRows.find(x => x.name == selectedTable?.name))) {
  //     selectedTable = flatTableRows.find(x => x.name == selectedTable?.name) || flatTableRows[0];
  //   }
  // }

  // $: {
  //   if (selectedTable && (!selectedTable.table || !selectedTable.file)) {
  //     const found = flatTableRows.find(x => x.name == selectedTable?.name);
  //     if (found && found.table && found.file) {
  //       selectedTable = found;
  //     }
  //   }
  // }

  $: changeTab(tabid, tab => ({
    ...tab,
    title: savedFile || savedFilePath ? tab.title : $values?.archiveFolder,
    props: {
      ...tab.props,
      conid: $values?.conid,
      database: $values?.database,
    },
  }));

  $: loadSelectedTable(selectedTable, reloadOverlayDataToken);

  let tableConfigs = {};
  let tableCaches = {};
  let extractedJslIds = {};
  let selectedTableFileData;
  let databaseKeysFromSelectedData;

  function getOperationLabel(label, checked, condition: { [key: string]: string }) {
    if (!checked) return null;
    if (!Object.values(condition || {}).find(x => x?.trim())) {
      return label;
    } else {
      return `${label} (${Object.entries(condition)
        .filter(([_key, value]) => value?.trim())
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')})`;
    }
  }

  function getOperationColumnsLabel(label, checked, columns?: string[]) {
    if (!checked) return null;
    if (!columns?.length) {
      return label;
    } else {
      return `${label} (${columns.join(', ')})`;
    }
  }

  function getAllOperationLabels(row) {
    return _.compact([
      getOperationLabel('Find', row.findExisting, row.findCondition),
      getOperationLabel('Create', row.createNew, row.createCondition),
      getOperationLabel('Update', row.updateExisting, row.updateCondition),
      getOperationColumnsLabel('Delete', row.deleteMissing, row.deleteRestrictionColumns),
    ]).join(', ');
  }
</script>

<FormProviderCore {values}>
  <ToolStripContainer>
    <div class="wrapper">
      <HorizontalSplitter isSplitter={settingsVisible} initialSizeRight={300}>
        <svelte:fragment slot="1">
          <VerticalSplitter>
            <svelte:fragment slot="1">
              <div class="flex1 scroll">
                <TableControl
                  selectable
                  clickable
                  stickyHeader
                  groupedRows={tableRows}
                  bind:selectedKey={selectedTableName}
                  selectionMode="key"
                  extractTableItemKey={row => row.name}
                  columns={[
                    { header: 'Archive file', fieldName: 'file', slot: 4 },
                    { header: 'Database table', fieldName: 'table', slot: 5 },
                    { header: 'Key', fieldName: 'matchColumns', slot: 3, slotKey: 'matchColumns' },
                    { header: 'Operation', fieldName: 'operation', slot: 1, slotKey: 'operationKey' },
                    { header: 'Actions', fieldName: 'actions', slot: 6 },
                  ]}
                  checkedKeys={checkedTables}
                  onSetCheckedKeys={keys => {
                    setEditorData(old => ({
                      ...old,
                      checkedTables: keys,
                    }));
                  }}
                  itemSupportsCheckbox={row => row.table && row.file}
                >
                  <svelte:fragment slot="1" let:row>
                    {getAllOperationLabels(row)}
                  </svelte:fragment>
                  <svelte:fragment slot="3" let:row>
                    {row.matchColumns.join(', ')}
                  </svelte:fragment>
                  <svelte:fragment slot="4" let:row>
                    {#if row.file}
                      <FontIcon icon="img archive" />
                      {row.file}
                    {/if}
                  </svelte:fragment>
                  <svelte:fragment slot="5" let:row>
                    {#if row.table}
                      <FontIcon icon="img table" />
                      {row.table}
                    {/if}
                  </svelte:fragment>
                  <svelte:fragment slot="6" let:row>
                    {#if row.file && !isArchiveReadOnly}
                      <Link
                        onClick={e => {
                          e.stopPropagation();

                          showModal(ConfirmModal, {
                            title: 'Delete file',
                            message: `Are you sure you want to delete file ${row.file}?`,
                            onConfirm: async () => {
                              await apiCall('archive/delete-file', {
                                folder: archiveFolder,
                                file: row.file,
                                fileType: 'jsonl',
                              });
                            },
                          });
                        }}>Delete file</Link
                      >
                    {/if}
                    {#if !row.file && row.table && !isArchiveReadOnly}
                      <Link
                        onClick={async e => {
                          e.stopPropagation();
                          await apiCall('archive/create-file', {
                            folder: archiveFolder,
                            file: row.name,
                            fileType: 'jsonl',
                            tableInfo: row.tableInfo,
                          });
                        }}>Create file</Link
                      >
                    {/if}
                  </svelte:fragment>
                </TableControl>
              </div>
            </svelte:fragment>
            <svelte:fragment slot="2">
              <TabControl
                tabs={[
                  runnerId && { label: 'Deploy logs', slot: 4 },
                  selectedTable?.file && selectedTable?.table && { label: 'Overlay view', slot: 3 },
                  selectedTable?.table && { label: 'Database table', slot: 1 },
                  selectedTable?.file && { label: 'Archive file', slot: 2 },
                ]}
              >
                <svelte:fragment slot="1">
                  {#if selectedTable}
                    {#key reloadDatabaseDataToken}
                      <TableDataGrid
                        {conid}
                        {database}
                        pureName={selectedTable.pureName}
                        config={tableConfigs[selectedTable.pureName] || createGridConfig()}
                        setConfig={value => {
                          const oldConfig = tableConfigs[selectedTable.pureName] || createGridConfig();
                          const newConfig = _.isFunction(value) ? value(oldConfig) : value;
                          tableConfigs = {
                            ...tableConfigs,
                            [selectedTable.pureName]: newConfig,
                          };
                        }}
                        cache={tableCaches[selectedTable.pureName] || createGridCache()}
                        setCache={value => {
                          const oldCache = tableConfigs[selectedTable.pureName] || createGridCache();
                          const newCache = _.isFunction(value) ? value(oldCache) : value;
                          tableCaches = {
                            ...tableCaches,
                            [selectedTable.pureName]: newCache,
                          };
                        }}
                        forceReadOnly
                        multipleGridsOnTab
                      />
                    {/key}
                  {/if}
                </svelte:fragment>
                <svelte:fragment slot="2">
                  {#if selectedTable}
                    {@const jslid = archiveFolder?.endsWith('.zip')
                      ? extractedJslIds?.[archiveFolder]?.[selectedTable.file]
                      : `archive://${archiveFolder}/${selectedTable.file}`}
                    {#if jslid}
                      {#key reloadOverlayDataToken}
                        <JslDataGrid {jslid} forceReadOnly multipleGridsOnTab />
                      {/key}
                    {/if}
                  {/if}
                </svelte:fragment>
                <svelte:fragment slot="3">
                  {#if selectedTable && selectedTableFileData && databaseKeysFromSelectedData}
                    {#key reloadDatabaseDataToken}
                      <TableDataGrid
                        {conid}
                        {database}
                        pureName={selectedTable.pureName}
                        config={tableConfigs[selectedTable.pureName] || createGridConfig()}
                        setConfig={value => {
                          const oldConfig = tableConfigs[selectedTable.pureName] || createGridConfig();
                          const newConfig = _.isFunction(value) ? value(oldConfig) : value;
                          tableConfigs = {
                            ...tableConfigs,
                            [selectedTable.pureName]: newConfig,
                          };
                        }}
                        cache={tableCaches[selectedTable.pureName] || createGridCache()}
                        setCache={value => {
                          const oldCache = tableConfigs[selectedTable.pureName] || createGridCache();
                          const newCache = _.isFunction(value) ? value(oldCache) : value;
                          tableCaches = {
                            ...tableCaches,
                            [selectedTable.pureName]: newCache,
                          };
                        }}
                        forceReadOnly
                        overlayDefinition={{
                          matchColumns: selectedTable.matchColumns,
                          overlayData: selectedTableFileData,
                          matchedDbKeys: databaseKeysFromSelectedData,
                        }}
                        multipleGridsOnTab
                        onGetSelectionMenu={cells => {
                          const columns = _.uniq(cells.map(x => x.column));
                          const rows = _.sortBy(_.uniq(cells.map(x => x.rowData)));
                          const res = [];
                          if (
                            !isArchiveReadOnly &&
                            cells.every(x => x.rowStatus.status == 'missing' || x.rowStatus.status == 'updated')
                          ) {
                            res.push({
                              text: `Write ${rows.length} rows, ${columns.length} columns to archive`,
                              onClick: () => saveCellsToArchive(columns, rows),
                            });
                          }
                          if (cells.every(x => x.rowStatus.status == 'inserted' || x.rowStatus.status == 'updated')) {
                            res.push({
                              text: `Write ${rows.length} rows, ${columns.length} columns to database`,
                              onClick: () => saveCellsToDatabase(cells, columns, rows),
                            });
                          }
                          return res.length > 0 ? res : null;
                        }}
                      />
                    {/key}
                  {/if}
                </svelte:fragment>
                <svelte:fragment slot="4">
                  {#if runnerId}
                    <SocketMessageView
                      eventName={runnerId ? `runner-info-${runnerId}` : null}
                      {executeNumber}
                      showNoMessagesAlert
                      showCaller
                    />
                  {/if}
                </svelte:fragment>
              </TabControl>
            </svelte:fragment>
          </VerticalSplitter>
        </svelte:fragment>
        <svelte:fragment slot="2">
          <WidgetColumnBar>
            <WidgetColumnBarItem
              title="Settings"
              name="dbCompareSettings"
              storageName="dbCompareSettingsWidget"
              onClose={() => {
                settingsVisible = false;
              }}
            >
              <WidgetsInnerContainer skipDefineWidth>
                <DataDeploySettings {selectedTable} {changeTable} {dbinfo} />
              </WidgetsInnerContainer>
            </WidgetColumnBarItem>
          </WidgetColumnBar>
        </svelte:fragment>
      </HorizontalSplitter>
    </div>
    <svelte:fragment slot="toolstrip">
      <ToolStripSaveButton idPrefix="datadeploy" />
      <ToolStripButton
        icon="icon settings"
        on:click={() => {
          settingsVisible = !settingsVisible;
        }}>Settings</ToolStripButton
      >
      <ToolStripButton
        icon="icon refresh"
        on:click={() => {
          reloadOverlayDataToken += 1;
          reloadDatabaseDataToken += 1;
        }}>Refresh</ToolStripButton
      >

      <ToolStripCommandButton command="dataDeploy.run" data-testid="DataDeployTab_importIntoDb" hideDisabled />
      <ToolStripCommandButton command="dataDeploy.kill" data-testid="DataDeployTab_kill" hideDisabled />
      <ToolStripCommandButton
        command="dataDeploy.generateShell"
        data-testid="DataDeployTab_generateShell"
        hideDisabled
      />
      <ToolStripCommandButton command="dataDeploy.generateSql" data-testid="DataDeployTab_generateSql" hideDisabled />
    </svelte:fragment>
  </ToolStripContainer>
</FormProviderCore>

{#if progressMessage}
  <LoadingInfo message={progressMessage} wrapper />
{/if}

<style>
  .wrapper {
    overflow: auto;
    flex: 1;
  }

  .flexcol {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .topbar {
    display: flex;
    margin: 10px 0px;
    width: 100%;
  }
  .arrow {
    font-size: 30px;
    color: var(--theme-icon-blue);
    align-self: center;
    position: relative;
    /* top: 10px; */
  }

  .deployButton {
    margin-left: 20px;
    margin-right: 20px;
  }

  .tableWrapper {
    position: relative;
    width: 100%;
    flex: 1;
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
  }
</style>
